import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { StrategyDspAudienceType } from "@front/m19-api-client";
import { AudienceId, DspStrategyService } from "@front/m19-services";
import {
  IButtonDirective,
  IStepperComponent,
  IStepperStep,
  ITabComponent,
  ITabsSelectorComponent,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspAudiencesSelectorComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audiences-selector.component";

export type ManualAudienceModalStep = "audience" | "blacklist";

export interface ManualAudienceModalData {
  mode: "creation" | "edit";
  isAudienceRequired?: boolean;
  whitelistAudiences?: AudienceId[][];
  blacklistAudiences?: AudienceId[][];
}

export interface ManualAudienceModalResult {
  whitelistAudiences: AudienceId[][];
  blacklistAudiences: AudienceId[][];
  whitelistChanges: {
    toAdd: AudienceId[][];
    toRemove: AudienceId[][];
  };
  blacklistChanges: {
    toAdd: AudienceId[][];
    toRemove: AudienceId[][];
  };
}

@Component({
  selector: "app-manual-audience-modal",
  templateUrl: "./manual-audience-modal.component.html",
  imports: [
    IButtonDirective,
    IStepperComponent,
    TranslocoDirective,
    DspAudiencesSelectorComponent,
    ITabsSelectorComponent,
    ITabComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManualAudienceModalComponent {
  static readonly defaultModalOptions: Omit<ModalOptions<ManualAudienceModalData>, "data"> = {
    size: "xl",
    height: "fill",
    padding: "horizontal",
    closeMode: "buttonOnly",
  };

  private readonly modalData = inject<ManualAudienceModalData | undefined>(MODAL_DATA);
  private readonly modalRef = inject<ModalRef<ManualAudienceModalResult>>(ModalRef);
  private readonly translocoService = inject(TranslocoService);

  readonly StrategyDspAudienceType = StrategyDspAudienceType;

  readonly mode = this.modalData?.mode ?? "creation";
  readonly currentStep = signal<ManualAudienceModalStep>("audience");
  readonly audiences = signal<AudienceId[][]>(copyAudienceGroups(this.modalData?.whitelistAudiences));
  readonly blacklistedAudiences = signal<AudienceId[][]>(copyAudienceGroups(this.modalData?.blacklistAudiences));

  readonly audiencesToAdd = computed(() =>
    getChangedAudiences(this.modalData?.whitelistAudiences ?? [], this.audiences()),
  );

  readonly audiencesToRemove = computed(() =>
    getChangedAudiences(this.audiences(), this.modalData?.whitelistAudiences ?? []),
  );

  readonly blacklistedAudiencesToAdd = computed(() =>
    getChangedAudiences(this.modalData?.blacklistAudiences ?? [], this.blacklistedAudiences()),
  );

  readonly blacklistedAudiencesToRemove = computed(() =>
    getChangedAudiences(this.blacklistedAudiences(), this.modalData?.blacklistAudiences ?? []),
  );

  readonly totalAudiences = computed(() => this.audiences().flat().length);
  readonly totalBlacklistedAudiences = computed(() => this.blacklistedAudiences().flat().length);

  readonly canGoNext = computed(() => {
    const audiences = this.audiences();
    return audiences.length > 0 && audiences[0].length > 0;
  });

  readonly canSaveAudiences = computed(() => {
    const audiences = this.audiences();
    const hasAudiences = audiences.length > 0 && audiences[0].length > 0;
    if (this.mode === "creation" || this.modalData?.isAudienceRequired) {
      return hasAudiences;
    }
    return true;
  });

  readonly steps = computed((): IStepperStep<ManualAudienceModalStep>[] => {
    const current = this.currentStep();
    return [
      {
        id: "audience",
        label: this.translocoService.translate("common.audience"),
        state: current === "audience" ? "current" : "default",
      },
      {
        id: "blacklist",
        label: this.translocoService.translate("common.blacklist"),
        state: current === "blacklist" ? "current" : "disabled",
      },
    ];
  });

  readonly hintMessage = computed(() =>
    this.currentStep() === "blacklist"
      ? this.translocoService.translate("dsp-strategy-page.max_audience_blacklist", {
          max: DspStrategyService.MAX_AUDIENCE_PER_GROUP,
        })
      : this.translocoService.translate("dsp-strategy-page.max_audience_warning", {
          maxGroupPerStrategy: DspStrategyService.MAX_AUDIENCE_GROUP_PER_STRATEGY,
          maxAudiencePerGroup: DspStrategyService.MAX_AUDIENCE_PER_GROUP,
        }),
  );

  next() {
    if (!this.canGoNext()) {
      return;
    }
    this.currentStep.set("blacklist");
  }

  back() {
    this.currentStep.set("audience");
  }

  save() {
    if (!this.canSaveAudiences()) {
      return;
    }
    this.modalRef.close({
      whitelistAudiences: this.audiences(),
      blacklistAudiences: this.blacklistedAudiences(),
      whitelistChanges: {
        toAdd: this.audiencesToAdd(),
        toRemove: this.audiencesToRemove(),
      },
      blacklistChanges: {
        toAdd: this.blacklistedAudiencesToAdd(),
        toRemove: this.blacklistedAudiencesToRemove(),
      },
    });
  }

  cancel() {
    this.modalRef.close();
  }
}

function copyAudienceGroups(groups: AudienceId[][] | undefined): AudienceId[][] {
  if (!groups?.length) {
    return [[]];
  }
  return groups.map((group) => [...group]);
}

export function getChangedAudiences(existingAudiences: AudienceId[][], newAudiences: AudienceId[][]): AudienceId[][] {
  const existingAudiencesChanged = existingAudiences.map((audienceGroup, index) =>
    getChangedAudienceGroups(audienceGroup, newAudiences[index] ?? []),
  );

  if (newAudiences.length > existingAudiences.length) {
    existingAudiencesChanged.push(...newAudiences.slice(existingAudiences.length));
  }

  return existingAudiencesChanged;
}

function getChangedAudienceGroups(existingAudienceGroup: AudienceId[], newAudienceGroup: AudienceId[]) {
  const existingAudienceIds = new Set(existingAudienceGroup.map((audience) => audience.audienceId));
  return newAudienceGroup.filter((audience) => !existingAudienceIds.has(audience.audienceId));
}
